import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CircularProgress } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import SystemUpdateIcon from '@material-ui/icons/SystemUpdate';

import compose from 'helpers/compose';
import MaterialTable from 'components/Table/Table';
import Background from 'components/Layout/Background';

import { RootStore } from 'stores/rootStore';
import { SignedDocumentsStore } from 'stores/account/signedDocumentsStore';
import useStyles from 'pages/Accounts/Detail/styles';

interface Props {
  rootStore: RootStore;
  accountId: string;
  backoffice?: boolean;
}

const PAGE_SIZE_KEY = 'signedDocumentsPageSize';

const SignedDocuments: React.FC<Props> = ({ rootStore, accountId, backoffice }) => {
  const { t } = useTranslation();
  const [store] = useState(new SignedDocumentsStore(rootStore, accountId, backoffice));
  const pageSizeStorageValue = localStorage.getItem(PAGE_SIZE_KEY);
  const pageSize = pageSizeStorageValue ? parseInt(pageSizeStorageValue) : 15;

  const classes = useStyles({});
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });

  useEffect(() => {
    accountId !== '' && store.fetchData(backoffice ? accountId : undefined);
  }, [store, accountId, backoffice]);

  return store.data || store.error ? (
    <Container maxWidth="md">
      <Background>
        {store.data && (
          <MaterialTable
            options={{
              header: false,
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              paging: store.data.length > pageSize,
            }}
            data={store.data}
            columns={[
              { field: 'filename' },
              {
                field: 'template',
                render: (document) => (
                  <Button
                    variant={mobile ? 'text' : 'outlined'}
                    color={mobile ? 'primary' : 'default'}
                    className={classes.downloadButton}
                    startIcon={
                      document.loading ? <CircularProgress size={20} /> : mobile ? <SystemUpdateIcon /> : <GetAppIcon />
                    }
                    onClick={() => store.downloadDocument(document, backoffice ? accountId : undefined)}
                  >
                    {!mobile && t('general_download')}
                  </Button>
                ),
                cellStyle: {
                  width: mobile ? '5%' : '10%',
                },
              },
            ]}
          />
        )}
        {store.error && backoffice && t('backOffice_bad_accountId')}
      </Background>
    </Container>
  ) : null;
};

export default compose(inject('rootStore'), observer)(SignedDocuments);
